import { useEffect, useMemo, useRef, useState } from "react";

export const filterProviderDomains = (providerDomains, query) => {
  const normalizedQuery = String(query || "")
    .trim()
    .toLowerCase();

  if (!normalizedQuery) return providerDomains;

  if (
    providerDomains.some(
      (providerDomain) =>
        providerDomain.uriHost.toLowerCase() === normalizedQuery
    )
  ) {
    return providerDomains;
  }

  return providerDomains.filter((providerDomain) =>
    providerDomain.uriHost.toLowerCase().includes(normalizedQuery)
  );
};

export const isNewProviderDomainValue = (providerDomains, value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();

  return (
    normalizedValue.length > 0 &&
    !providerDomains.some(
      (providerDomain) =>
        providerDomain.uriHost.toLowerCase() === normalizedValue
    )
  );
};

const ProviderDomainCombobox = ({
  disabled = false,
  loading = false,
  onChange,
  providerDomains,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);
  const options = useMemo(
    () => filterProviderDomains(providerDomains, value),
    [providerDomains, value]
  );
  const newProviderDomainValue = String(value || "").trim();
  const canUseNewProviderDomain = isNewProviderDomainValue(
    providerDomains,
    value
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={pickerRef}>
      <i className="fas fa-globe absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-gray-500 dark:text-gray-300 text-sm pointer-events-none" />
      <input
        aria-autocomplete="list"
        aria-controls="reseller-provider-domain-options"
        aria-expanded={open}
        autoComplete="off"
        className="input pl-9 pr-10"
        disabled={disabled}
        placeholder={
          loading
            ? "Loading provider hosts..."
            : providerDomains.length === 0
              ? "Enter a provider host *"
              : "Search, select, or enter a provider host *"
        }
        role="combobox"
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      <button
        aria-label="Open provider host options"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-sm disabled:opacity-50"
        disabled={disabled || loading}
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <i className={`fas fa-chevron-${open ? "up" : "down"} text-xs`} />
      </button>

      {open && !disabled && (
        <div
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-800"
          id="reseller-provider-domain-options"
          role="listbox"
        >
          <div className="max-h-52 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2" />
                Loading provider hosts...
              </div>
            ) : (
              <>
                {options.map((providerDomain) => {
                  const selected =
                    String(value || "")
                      .trim()
                      .toLowerCase() === providerDomain.uriHost.toLowerCase();

                  return (
                    <button
                      aria-selected={selected}
                      className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${
                        selected
                          ? "bg-primary-500/10 text-primary-700 dark:text-primary-300"
                          : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5"
                      }`}
                      key={providerDomain.id}
                      role="option"
                      type="button"
                      onClick={() => {
                        onChange(providerDomain.uriHost);
                        setOpen(false);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      <i className="fas fa-server text-xs text-gray-400" />
                      <span className="truncate font-medium">
                        {providerDomain.uriHost}
                      </span>
                      {selected && (
                        <i className="fas fa-check ml-auto text-xs" />
                      )}
                    </button>
                  );
                })}
                {canUseNewProviderDomain && (
                  <button
                    aria-selected="false"
                    className="w-full px-3 py-2.5 text-left text-sm text-primary-700 hover:bg-primary-500/10 dark:text-primary-300 transition-colors flex items-center gap-2 border-t border-gray-100 dark:border-white/5"
                    role="option"
                    type="button"
                    onClick={() => {
                      onChange(newProviderDomainValue);
                      setOpen(false);
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    <i className="fas fa-plus-circle text-xs" />
                    <span className="min-w-0">
                      <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Use new provider host
                      </span>
                      <span className="block truncate font-medium">
                        {newProviderDomainValue}
                      </span>
                    </span>
                  </button>
                )}
                {!canUseNewProviderDomain && options.length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    Enter a provider URL to add with this playlist.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderDomainCombobox;
